import React from 'react';
import Badge from '../ui/Badge';
import SidebarCard from './SidebarCard';
import Field from './Field';
import { isoToLocalInput, localInputToIso } from '../pageStatus';

const pad = (n) => String(n).padStart(2, '0');
export const formatClock = (date) => (date ? `${pad(date.getHours())}:${pad(date.getMinutes())}` : '');

/**
 * Status, publish date, save/publish actions, last-saved time, preview and
 * live link. `onChange(field, value)` writes into the page draft.
 *
 * revisionMode: the page is live, so edits are not saved over it. Save writes
 * a pending revision (visible only in preview), "Publish now" makes the
 * revision the live page, "Discard revision" throws it away. Drafts and
 * scheduled pages keep saving directly.
 */
export default function PublishCard({
  page,
  pageState,
  isNewPage,
  saving,
  dirty,
  lastSavedAt,
  canPreview,
  liveUrl,
  open,
  onToggle,
  onChange,
  onSave,
  onPublishNow,
  onPreview,
  onSitePreview,
  revisionMode = false,
  hasRevision = false,
  onPublishRevision,
  onDiscardRevision,
}) {
  const showPublishNow = !revisionMode && (pageState === 'draft' || pageState === 'scheduled');
  return (
    <SidebarCard
      id="publish"
      title="Publish"
      open={open}
      onToggle={onToggle}
      badge={(
        <span style={{ display: 'inline-flex', gap: 4 }}>
          <Badge state={pageState} />
          {hasRevision && <Badge state="info">Pending revision</Badge>}
        </span>
      )}
    >
      {revisionMode && (
        <p className="muted" style={{ marginTop: 0, fontSize: 12 }}>
          {hasRevision
            ? 'Pending revision: your saved edits show only in preview. The live page is unchanged until you publish.'
            : 'This page is live. Edits are saved as a revision you can preview before publishing.'}
        </p>
      )}
      <Field
        id="publish-status"
        label="Status"
        hint={revisionMode ? 'A live page keeps its status while you work on a revision. Unpublish it from the page list.' : undefined}
      >
        <select
          id="publish-status"
          className="select"
          value={page.status || 'draft'}
          disabled={revisionMode}
          onChange={(e) => onChange('status', e.target.value)}
        >
          <option value="draft">Draft</option>
          <option value="published">Published</option>
          <option value="archived">Archived</option>
        </select>
      </Field>

      <Field
        id="publish-date"
        label="Publish date"
        hint={page.status === 'draft' && pageState === 'scheduled' && page.scheduled_for
          ? `Queued: the daily publish queue puts this live ${new Date(page.scheduled_for).toLocaleString()} and sets this date then.`
          : pageState === 'scheduled' ? 'In the future: the page is scheduled.' : 'Shown as the article date. A future date schedules the page.'}
      >
        <input
          id="publish-date"
          type="datetime-local"
          className="input"
          value={isoToLocalInput(page.published_at)}
          onChange={(e) => onChange('published_at', localInputToIso(e.target.value))}
        />
      </Field>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {showPublishNow && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={onPublishNow}
            disabled={saving}
            title="Set status to Published (keeping a past date, else now) and save"
          >
            Publish now
          </button>
        )}
        <button
          type="button"
          className={showPublishNow ? 'btn btn-secondary' : 'btn btn-primary'}
          onClick={onSave}
          disabled={saving}
          title={revisionMode ? 'Save as a pending revision; the live page is unchanged (Ctrl+S / Cmd+S)' : 'Save (Ctrl+S / Cmd+S)'}
        >
          {saving ? 'Saving…' : isNewPage ? 'Create page' : revisionMode ? 'Save as revision' : 'Save'}
        </button>
        {revisionMode && (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onPublishRevision}
            disabled={saving || (!hasRevision && !dirty)}
            title="Save any edits to the revision, then make it the live page"
          >
            Publish now
          </button>
        )}
        {revisionMode && hasRevision && (
          <button
            type="button"
            className="btn btn-ghost"
            style={{ color: 'var(--color-danger)' }}
            onClick={onDiscardRevision}
            disabled={saving}
            title="Throw away the pending revision; the live page is unchanged"
          >
            Discard revision
          </button>
        )}
      </div>
      {!isNewPage && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 8 }}>
          {onSitePreview && (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={onSitePreview}
              title="Open the saved page on its own site in preview (new tab). Drafts and pending revisions show for you only."
            >
              Preview ↗
            </button>
          )}
          {canPreview && (
            <button type="button" className="btn btn-ghost" onClick={onPreview} title="Preview in the console, on the development renderer">
              Dev preview
            </button>
          )}
        </div>
      )}

      <div style={{ marginTop: 10, fontSize: 12, color: 'var(--color-muted)', display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span aria-live="polite">
          {dirty ? 'Unsaved changes' : lastSavedAt ? `Last saved ${formatClock(lastSavedAt)}` : isNewPage ? 'Not saved yet' : 'No changes since load'}
        </span>
        {pageState === 'published' && liveUrl && (
          <a href={liveUrl} target="_blank" rel="noopener noreferrer">
            View live page ↗
          </a>
        )}
      </div>
    </SidebarCard>
  );
}
